import {
  Document,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TextRun,
  WidthType,
  ShadingType,
  AlignmentType,
  ImageRun,
  VerticalAlign,
  HeightRule,
  TableLayoutType,
  Header,
} from 'docx';
import {
  PAPER_TABS,
  PAPER_CONFIG,
  AUDIO_PAPER_TYPE,
} from '../../../data/common';
import {
  strParse,
  makeFontSizeDiff,
  makeUnderLineForDocx,
} from '../../../lib/util';
import { base64Watermark } from '../../../data/base64Watermark';

const borderTransparent = {
  top: { color: 'FFFFFF', size: 0, style: 'none' },
  bottom: { color: 'FFFFFF', size: 0, style: 'none' },
  left: { color: 'FFFFFF', size: 0, style: 'none' },
  right: { color: 'FFFFFF', size: 0, style: 'none' },
};
const borderBottom = {
  top: { color: 'FFFFFF', size: 0, style: 'none' },
  bottom: { color: '000000', size: 6, style: 'single' },
  left: { color: 'FFFFFF', size: 0, style: 'none' },
  right: { color: 'FFFFFF', size: 0, style: 'none' },
};
const MAX_WIDTH = 4700;
const INDEX_WIDTH = MAX_WIDTH * 0.1;
const QUESTION_WIDTH = MAX_WIDTH * 0.85;

// 헤더 역할을 하는 표 생성
const createHeaderTable = (
  orgName,
  className,
  teacherName,
  paperName,
  base64Img,
  fontSize,
  logoAppearYN,
) => {
  const getImageType = imageData => {
    const header = imageData.slice(0, 4).toString();
    if (header.startsWith('137,80,78,71')) return 'png';
    if (header.startsWith('255,216,255')) return 'jpg';
    if (header.startsWith('71,73,70')) return 'gif';
    return 'jpg';
  };

  return new Table({
    rows: [
      new TableRow({
        children: [
          // 첫 번째 셀: 로고 이미지
          new TableCell({
            children: [
              new Paragraph({
                children:
                  base64Img && logoAppearYN
                    ? [
                        new ImageRun({
                          data: base64Img,
                          transformation: {
                            width: 90,
                            height: 27,
                          },
                          type: getImageType(base64Img), // 반드시 설정해야 경고창 뜨지 않음.
                        }),
                      ]
                    : [],
                alignment: AlignmentType.LEFT,
              }),
            ],
            borders: borderBottom,
            verticalAlign: VerticalAlign.CENTER,
            width: {
              size: base64Img !== null && logoAppearYN ? 12 : 0,
              type: WidthType.PERCENTAGE, // 셀 너비 비율
            },
          }),
          // 두 번째 셀: 학원명,클래스명,교사명
          new TableCell({
            children: [
              new Paragraph({
                children:
                  base64Img === null || (base64Img !== null && !logoAppearYN)
                    ? [
                        new TextRun({
                          text: `${orgName}  `,
                          size: fontSize + 2,
                        }),
                        new TextRun({
                          text: className === '' ? '' : className,
                          size: fontSize + 2,
                          break: 1,
                        }),
                        new TextRun({
                          text: teacherName === '' ? '' : teacherName,
                          size: fontSize + 2,
                          break: 1,
                        }),
                      ]
                    : [
                        new TextRun({
                          text: className === '' ? '' : className,
                          size: fontSize + 2,
                        }),
                        new TextRun({
                          text: teacherName === '' ? '' : teacherName,
                          size: fontSize + 2,
                          break: 1,
                        }),
                      ],
              }),
            ],
            borders: borderBottom,
            verticalAlign: VerticalAlign.CENTER,
            width: {
              size: base64Img !== null ? 14 : 16,
              type: WidthType.PERCENTAGE,
            },
          }),
          // 세번째 셀: 시험지명
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: paperName,
                    bold: true,
                    size: fontSize + 8,
                  }),
                ],
                alignment: AlignmentType.CENTER,
              }),
            ],
            borders: borderBottom,
            verticalAlign: VerticalAlign.CENTER,
            width: {
              size: base64Img !== null ? 39 : 58,
              type: WidthType.PERCENTAGE,
            },
          }),
          // 네번째 셀: 회색 음영 표시
          new TableCell({
            children: [
              new Paragraph({
                text: '',
              }),
            ],
            borders: borderBottom,
            shading: {
              type: ShadingType.CLEAR,
              color: 'D9D9D9', // 회색 음영
              fill: 'D9D9D9',
            },
            width: {
              size: 25,
              type: WidthType.PERCENTAGE,
            },
          }),
        ],
      }),
    ],
    width: {
      size: 100,
      type: WidthType.PERCENTAGE, // 테이블 전체 너비를 100%로 설정
    },
  });
};

// 문제 제목 생성
const createSubject = (subjectText, fontSize, type) => {
  let result = [];
  if (type === 'K') {
    result = [
      new TextRun({
        text: '',
        break: 1,
      }),
      new TextRun({
        text: `※ `,
        bold: true,
        size: fontSize,
      }),
      ...makeFontSizeDiff(subjectText).map(text => {
        return new TextRun({
          text: text.startsWith('(') && text.endsWith(')') ? ` ${text}` : text,
          bold: true,
          size:
            text.startsWith('(') && text.endsWith(')')
              ? fontSize - 2
              : fontSize,
        });
      }),
    ];
  } else if (type === 'L') {
    const targetWord = subjectText?.includes('유의어') ? '유의어' : '반의어';
    result = [
      new TextRun({
        text: '',
        break: 1,
      }),
      new TextRun({
        text: `※ `,
        bold: true,
        size: fontSize,
      }),
      ...makeUnderLineForDocx(subjectText, targetWord).map(text => {
        return new TextRun({
          text: text,
          bold: true,
          size: fontSize,
          underline: targetWord === text ? {} : { type: 'none' },
        });
      }),
    ];
  } else {
    result = [
      new TextRun({
        text: '',
        break: 1,
      }),
      new TextRun({
        text: `※ ${subjectText}`,
        bold: true,
        size: fontSize,
      }),
    ];
  }
  return new Paragraph({
    children: result,
    bold: true,
    alignment: AlignmentType.LEFT,
    spacing: { after: 130 },
  });
};

// A타입 텍스트 항목 생성
const createAtypeItem = (index, text, paperTab, fontSize, lineSpacing) => {
  // '시험지', '정답표시 시험지'
  let result;
  if (
    paperTab === PAPER_TABS.TEST_PAPER ||
    paperTab === PAPER_TABS.ANSWER_TEST_PAPER
  ) {
    result = new Table({
      rows: [
        new TableRow({
          children: [
            // 번호 셀 (10%)
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${index}.`,
                      bold: false,
                      size: fontSize,
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.LEFT,
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.TOP,
              width: {
                size: 1000, // 10% 너비
                type: WidthType.DXA,
              },
            }),
            // 단어 셀 (45%)
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: text.question,
                      bold: false,
                      size: fontSize,
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.LEFT,
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.TOP,
              width: {
                size: 4500, // 45% 너비
                type: WidthType.DXA,
              },
            }),
            // 뜻표시 셀 (45%)
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text:
                        paperTab === PAPER_TABS.TEST_PAPER ? '' : text.answer,
                      bold: false,
                      size: fontSize,
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.CENTER,
                }),
              ],
              borders: borderBottom,
              verticalAlign: VerticalAlign.CENTER,
              width: {
                size: 4500, // 45% 너비
                type: WidthType.DXA,
              },
            }),
          ],
          height: {
            value: lineSpacing, // 높이 설정 (Twip 단위: 500 = 약 0.35인치)
            rule: HeightRule.ATLEAST, // 최소 높이로 설정
          },
        }),
      ],
      width: {
        size: 100,
        type: WidthType.PERCENTAGE,
      },
      layout: TableLayoutType.FIXED, // 테이블 레이아웃을 고정
    });
  }
  // '정답만' 시험지
  else if (paperTab === PAPER_TABS.ANSWER) {
    result = new Table({
      rows: [
        new TableRow({
          children: [
            // 번호 셀 (10%)
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${index}.`,
                      bold: false,
                      size: fontSize,
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.LEFT,
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.TOP,
              width: {
                size: 1000, // 10% 너비
                type: WidthType.DXA,
              },
            }),
            // 뜻표시 셀 (90%)
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: text.answer,
                      bold: false,
                      size: fontSize,
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.LEFT,
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.CENTER,
              width: {
                size: 9000, // 45% 너비
                type: WidthType.DXA,
              },
            }),
          ],
          height: {
            value: lineSpacing, // 높이 설정 (Twip 단위: 500 = 약 0.35인치)
            rule: HeightRule.ATLEAST, // 최소 높이로 설정
          },
        }),
      ],
      width: {
        size: 100,
        type: WidthType.PERCENTAGE,
      },
      layout: TableLayoutType.FIXED, // 테이블 레이아웃을 고정
    });
  }
  return result;
};

// B타입 텍스트 항목 생성
const createBtypeItem = (
  index,
  text,
  paperTab,
  fontSize,
  lineSpacing,
  vocaFirstWordShow,
) => {
  // '시험지', '정답표시 시험지'
  let result;
  if (
    paperTab === PAPER_TABS.TEST_PAPER ||
    paperTab === PAPER_TABS.ANSWER_TEST_PAPER
  ) {
    result = new Table({
      rows: [
        new TableRow({
          children: [
            // 번호 셀 (10%)
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${index}.`, // 번호
                      bold: false,
                      size: fontSize,
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.LEFT,
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.TOP, // 수직 정렬: 가운데
              width: {
                size: 1000, // 10% 너비
                type: WidthType.DXA,
              },
            }),
            // 뜻 셀 (45%)
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: text.question, // 왼쪽 텍스트
                      bold: false,
                      size: fontSize,
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.LEFT, // 왼쪽 정렬
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.TOP, // 수직 정렬: 가운데
              width: {
                size: 4500, // 45% 너비
                type: WidthType.DXA,
              },
            }),
            // 단어표시 셀 (45%)
            new TableCell({
              children: [
                new Paragraph({
                  children: vocaFirstWordShow
                    ? [
                        new TextRun({
                          text:
                            paperTab === PAPER_TABS.TEST_PAPER
                              ? text.answer[0]
                              : text.answer, // 오른쪽 밑줄 텍스트
                          bold: false,
                          size: fontSize,
                        }),
                      ]
                    : [
                        new TextRun({
                          text:
                            paperTab === PAPER_TABS.TEST_PAPER
                              ? ''
                              : text.answer, // 오른쪽 밑줄 텍스트
                          bold: false,
                          size: fontSize,
                        }),
                      ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment:
                    paperTab === PAPER_TABS.TEST_PAPER && vocaFirstWordShow
                      ? AlignmentType.LEFT
                      : AlignmentType.CENTER,
                }),
              ],
              borders: borderBottom,
              verticalAlign: VerticalAlign.BOTTOM, // 수직 정렬: 가운데
              width: {
                size: 4500, // 45% 너비
                type: WidthType.DXA,
              },
            }),
          ],
          height: {
            value: lineSpacing, // 높이 설정 (Twip 단위: 500 = 약 0.35인치)
            rule: HeightRule.ATLEAST, // 높이를 자동으로 설정
          },
        }),
      ],
      width: {
        size: 100,
        type: WidthType.PERCENTAGE, // 테이블 너비를 100%로 설정
      },
      layout: TableLayoutType.FIXED, // 테이블 레이아웃을 고정
    });
  }
  // '정답만' 시험지
  else if (paperTab === PAPER_TABS.ANSWER) {
    result = new Table({
      rows: [
        new TableRow({
          children: [
            // 번호 셀 (10%)
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${index}.`, // 번호
                      bold: false,
                      size: fontSize,
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.LEFT,
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.TOP, // 수직 정렬: 가운데
              width: {
                size: 1000, // 10% 너비
                type: WidthType.DXA,
              },
            }),
            // 단어표시 셀 (90%)
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text:
                        paperTab === PAPER_TABS.TEST_PAPER ? '' : text.answer, // 오른쪽 밑줄 텍스트
                      bold: false,
                      size: fontSize,
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.LEFT,
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.CENTER, // 수직 정렬: 가운데
              width: {
                size: 9000, // 90% 너비
                type: WidthType.DXA,
              },
            }),
          ],
          height: {
            value: lineSpacing, // 높이 설정 (Twip 단위: 500 = 약 0.35인치)
            rule: HeightRule.ATLEAST, // 높이를 자동으로 설정
          },
        }),
      ],
      width: {
        size: 100,
        type: WidthType.PERCENTAGE, // 테이블 너비를 100%로 설정
      },
      layout: TableLayoutType.FIXED, // 테이블 레이아웃을 고정
    });
  }
  return result;
};

// C타입 텍스트 항목 생성
const createCtypeItem = (
  index,
  text,
  paperTab,
  fontSize,
  lineSpacing,
  vocaFirstWordShow,
) => {
  // '시험지', '정답표시 시험지'
  let result;
  if (
    paperTab === PAPER_TABS.TEST_PAPER ||
    paperTab === PAPER_TABS.ANSWER_TEST_PAPER
  ) {
    result = new Table({
      rows: [
        new TableRow({
          children: [
            // 번호 셀
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${index}. `, // 왼쪽 인덱스
                      bold: false,
                      size: fontSize,
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.LEFT, // 왼쪽 정렬
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.TOP, // 수직 정렬: 가운데
              width: {
                size: 1000,
                type: WidthType.DXA,
              },
            }),
            // 문제 셀
            new TableCell({
              children: [
                ...text.questions.map(subText => {
                  return new Paragraph({
                    children:
                      paperTab === PAPER_TABS.TEST_PAPER
                        ? [
                            new TextRun({
                              text: subText.questionTranslation, // 상단 한글해석
                              bold: false,
                              size: fontSize,
                            }),
                            ...(subText.questionSentence
                              ?.split(/(\{[^{}]*\}|[^\s{}]+)/) // {} 내부와 외부를 구분하면서 split
                              .filter(Boolean) // 빈 문자열 제거
                              ?.flatMap((word, wIdx) => {
                                if (
                                  word.startsWith('{') &&
                                  word.endsWith('}')
                                ) {
                                  // {}로 둘러싸인 경우 처리
                                  const editWord = word
                                    .replace(/[{}]/g, '') // {} 제거
                                    .split('^') // ^ 기준으로 문자열 분할
                                    .reduce((acc, cur, index, arr) => {
                                      acc.push(cur);
                                      if (index < arr.length - 1) {
                                        acc.push(''); // 마지막 요소를 제외한 모든 요소 사이에 " " 추가
                                      }
                                      return acc;
                                    }, []);
                                  const words = editWord.map(sWord => {
                                    let result = {};
                                    if (vocaFirstWordShow) {
                                      result = new TextRun({
                                        text:
                                          sWord !== ''
                                            ? sWord[0] + '                '
                                            : ' ',
                                        bold: false,
                                        size: fontSize,
                                        underline: {
                                          type:
                                            sWord !== '' ? 'single' : 'none',
                                          color: '000000',
                                        },
                                        break: wIdx === 0 && 1,
                                      });
                                    } else {
                                      result = new TextRun({
                                        text:
                                          sWord !== ''
                                            ? '___________________'
                                            : ' ',
                                        bold: false,
                                        size: fontSize,
                                        underline: {
                                          type: 'none',
                                          color: '000000',
                                        },
                                        break: wIdx === 0 && 1,
                                      });
                                    }
                                    return result;
                                  });
                                  return words;
                                } else {
                                  // 일반 단어 처리
                                  return [
                                    new TextRun({
                                      text: word + '',
                                      bold: false,
                                      size: fontSize,
                                      underline: {
                                        type: 'none',
                                        color: '000000',
                                      },
                                      break: wIdx === 0 && 1, // 첫 번째 단어 줄바꿈
                                    }),
                                  ];
                                }
                              }) ?? []),
                          ]
                        : [
                            new TextRun({
                              text: subText.questionTranslation, // 상단 한글해석
                              bold: false,
                              size: fontSize,
                            }),
                            ...(subText.questionSentence
                              ?.split(/(\{[^{}]*\}|[^\s{}]+)/) // {} 내부와 외부를 구분하면서 split
                              .filter(Boolean) // 빈 문자열 제거
                              ?.map((word, wIdx) => {
                                if (
                                  word.startsWith('{') &&
                                  word.endsWith('}')
                                ) {
                                  // {} 내부 텍스트 처리
                                  const cleanedText = word
                                    .replace(/[{}]/g, '') // {} 제거
                                    .replace(/[!@#$%&*()?><":;.,]/g, '') // 특수문자 제거
                                    .split('^');
                                  // ^로 분리 후 각 단어를 밑줄 처리
                                  return cleanedText.flatMap(
                                    (cleanWord, idx) => [
                                      new TextRun({
                                        text: cleanWord, // 단어
                                        bold: false,
                                        size: fontSize,
                                        underline: {
                                          type: 'single',
                                          color: '000000',
                                        },
                                        break: wIdx === 0 && 1,
                                      }),
                                      new TextRun({
                                        text:
                                          idx === cleanedText?.length - 1
                                            ? ''
                                            : ' ', // 공백을 별도의 TextRun으로 추가
                                        bold: false,
                                        size: fontSize,
                                      }),
                                    ],
                                  );
                                } else {
                                  // 일반 단어 처리
                                  return new TextRun({
                                    text: `${word}`,
                                    bold: false,
                                    size: fontSize,
                                    break: wIdx === 0 && 1, // 첫 번째 단어 줄바꿈
                                    underline: {
                                      type: 'none',
                                      color: '000000',
                                    },
                                  });
                                }
                              })
                              ?.flat() ?? []),
                          ],
                    spacing: {
                      line: lineSpacing,
                    },
                    alignment: AlignmentType.LEFT, // 왼쪽 정렬
                  });
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.TOP, // 수직 정렬: 가운데
              width: {
                size: 9000,
                type: WidthType.DXA,
              },
            }),
          ],
          height: {
            value: lineSpacing, // 높이 설정 (Twip 단위: 500 = 약 0.35인치)
            rule: HeightRule.ATLEAST, // 높이를 자동으로 설정
          },
        }),
      ],
      width: {
        size: 100,
        type: WidthType.PERCENTAGE,
      },
      layout: TableLayoutType.FIXED, // 테이블 레이아웃을 고정
    });
  }
  // '정답만' 시험지
  else if (paperTab === PAPER_TABS.ANSWER) {
    result = new Table({
      rows: [
        new TableRow({
          children: [
            // 번호 셀
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${index}. `, // 왼쪽 인덱스
                      bold: false,
                      size: fontSize,
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.LEFT, // 왼쪽 정렬
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.TOP, // 수직 정렬: 가운데
              width: {
                size: 1000,
                type: WidthType.DXA,
              },
            }),
            // 문제 셀
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: text.word, // 왼쪽 인덱스
                      bold: false,
                      size: fontSize,
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.LEFT, // 왼쪽 정렬
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.TOP, // 수직 정렬: 가운데
              width: {
                size: 9000,
                type: WidthType.DXA,
              },
            }),
          ],
          height: {
            value: lineSpacing, // 높이 설정 (Twip 단위: 500 = 약 0.35인치)
            rule: HeightRule.ATLEAST, // 높이를 자동으로 설정
          },
        }),
      ],
      width: {
        size: 100,
        type: WidthType.PERCENTAGE,
      },
      layout: TableLayoutType.FIXED, // 테이블 레이아웃을 고정
    });
  }
  return result;
};

// D타입 텍스트 항목 생성
const createDtypeItem = (
  index,
  text,
  paperTab,
  fontSize,
  lineSpacing,
  vocaFirstWordShow,
) => {
  // '시험지', '정답표시 시험지'
  let result;
  if (
    paperTab === PAPER_TABS.TEST_PAPER ||
    paperTab === PAPER_TABS.ANSWER_TEST_PAPER
  ) {
    result = new Table({
      rows: [
        new TableRow({
          children: [
            // 번호 셀
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${index}. `, // 왼쪽 인덱스
                      bold: false,
                      size: fontSize,
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.LEFT,
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.TOP,
              width: {
                size: MAX_WIDTH / 10, // 두단: 450(10%크기),
                type: WidthType.DXA,
              },
            }),
            // 문제셀
            new TableCell({
              children: [
                new Table({
                  rows: [
                    // 1. 첫 번째 행: 영단어 + 한글 해석 (컬럼 3개 병합)
                    new TableRow({
                      children: [
                        new TableCell({
                          children: [
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: strParse(text.question),
                                  bold: false,
                                  size: fontSize,
                                }),
                              ],
                              spacing: {
                                line: lineSpacing,
                              },
                              alignment: AlignmentType.LEFT,
                            }),
                          ],
                          borders: borderTransparent,
                          verticalAlign: VerticalAlign.TOP,
                          columnSpan: 3, // 열 3개를 병합
                          width: {
                            size: MAX_WIDTH - MAX_WIDTH / 10, // 두단: 4050(90%크기)
                            type: WidthType.DXA,
                          },
                        }),
                      ],
                    }),
                    // 2. (유) ___ 뜻 행 추가
                    ...text.synonyms.map(word => {
                      return new TableRow({
                        children: [
                          // (유)(반) 셀
                          new TableCell({
                            children: [
                              new Paragraph({
                                children: [
                                  new TextRun({
                                    text: `- (${word.type}) `, // (유)(반)
                                    bold: false,
                                    size: fontSize,
                                  }),
                                ],
                                spacing: {
                                  line: lineSpacing,
                                },
                              }),
                            ],
                            borders: borderTransparent,
                            verticalAlign: VerticalAlign.TOP,
                            width: {
                              size: (MAX_WIDTH - MAX_WIDTH / 10) * 0.15, // 두단: 행 4050시의 15%
                              type: WidthType.DXA,
                            },
                          }),
                          // 정답 셀
                          new TableCell({
                            children: [
                              new Table({
                                rows: [
                                  // 정답 표시 + 밑줄 추가
                                  new TableRow({
                                    children: [
                                      new TableCell({
                                        children: vocaFirstWordShow
                                          ? [
                                              new Paragraph({
                                                children: [
                                                  new TextRun({
                                                    text:
                                                      paperTab ===
                                                      PAPER_TABS.TEST_PAPER
                                                        ? word.answer[0]
                                                        : word.answer, // 첫 번째 행의 정답 필드
                                                    bold: false,
                                                    size: fontSize,
                                                  }),
                                                ],
                                                spacing: {
                                                  line: lineSpacing,
                                                },
                                                alignment:
                                                  paperTab ===
                                                  PAPER_TABS.TEST_PAPER
                                                    ? AlignmentType.LEFT
                                                    : AlignmentType.CENTER,
                                              }),
                                            ]
                                          : [
                                              new Paragraph({
                                                children: [
                                                  new TextRun({
                                                    text:
                                                      paperTab ===
                                                      PAPER_TABS.TEST_PAPER
                                                        ? ''
                                                        : word.answer, // 첫 번째 행의 정답 필드
                                                    bold: false,
                                                    size: fontSize,
                                                  }),
                                                ],
                                                spacing: {
                                                  line: lineSpacing,
                                                },
                                                alignment: AlignmentType.CENTER,
                                              }),
                                            ],
                                        borders: borderBottom, // 밑줄 추가
                                        verticalAlign: VerticalAlign.BOTTOM,
                                        columnSpan: 2, // 모든 칼럼을 하나로 합침
                                      }),
                                    ],
                                  }),
                                  // 두 번째 행: 비어 있는 셀 (내용 없음)
                                  new TableRow({
                                    children: [
                                      new TableCell({
                                        children: [
                                          new Paragraph({
                                            children: [
                                              new TextRun({
                                                text: ' ', // 최소한의 내용 추가 (완전히 비우면 줄어들지 않을 수 있음)
                                                size: 1, // 폰트 최소 크기 설정 (1pt)
                                              }),
                                            ],
                                            spacing: {
                                              line: 100, // 줄 간격 최소화 (조정 가능)
                                            },
                                          }),
                                        ],
                                        borders: borderTransparent, // 테두리 없음
                                        verticalAlign: VerticalAlign.BOTTOM,
                                        columnSpan: 2, // 칼럼 병합 유지
                                      }),
                                    ],
                                    height: {
                                      value: 50, // 최소 높이 설정 (조정 가능, 기본 Twip 단위)
                                      rule: HeightRule.EXACT, // 정확한 높이로 설정하여 줄어들도록 강제
                                    },
                                  }),
                                ],
                                borders: borderTransparent, // 전체 테이블의 테두리 없음
                                width: {
                                  size: (MAX_WIDTH - MAX_WIDTH / 10) * 0.4, // 두단: 4050시 40% 크기
                                  type: WidthType.DXA,
                                },
                              }),
                            ],
                            borders: borderTransparent,
                            width: {
                              size: (MAX_WIDTH - MAX_WIDTH / 10) * 0.4, // 두단: 4050시 40% 크기
                              type: WidthType.DXA,
                            },
                          }),
                          // 뜻 셀
                          new TableCell({
                            children: [
                              new Paragraph({
                                children: [
                                  new TextRun({
                                    text: `${word.meaning}`, // 뜻
                                    bold: false,
                                    size: fontSize,
                                  }),
                                ],
                                spacing: {
                                  line: lineSpacing,
                                },
                              }),
                            ],
                            // rowSpan: 2,
                            borders: borderTransparent,
                            verticalAlign: VerticalAlign.TOP,
                            width: {
                              size: (MAX_WIDTH - MAX_WIDTH / 10) * 0.45,
                              type: WidthType.DXA,
                            },
                          }),
                        ],
                        verticalAlign: VerticalAlign.CENTER,
                        width: {
                          size: MAX_WIDTH - MAX_WIDTH / 10, // 두단: 4050(90%크기),
                          type: WidthType.DXA,
                        },
                      });
                    }),
                  ],
                  borders: borderTransparent,
                }),
              ],
              borders: borderTransparent,
              width: {
                size: MAX_WIDTH - MAX_WIDTH / 10, // 두단: 4050(90%크기)
                type: WidthType.DXA,
              },
            }),
          ],
          height: {
            value: lineSpacing, // 높이 설정 (Twip 단위: 500 = 약 0.35인치)
            rule: HeightRule.ATLEAST, // 높이를 자동으로 설정
          },
        }),
      ],
      width: {
        size: MAX_WIDTH, // 4500
        type: WidthType.DXA,
      },
      layout: TableLayoutType.FIXED, // 테이블 레이아웃을 고정
    });
  }
  // '정답만' 시험지
  else if (paperTab === PAPER_TABS.ANSWER) {
    result = new Table({
      rows: [
        new TableRow({
          children: [
            // 번호 셀
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${index}. `, // 왼쪽 인덱스
                      bold: false,
                      size: fontSize,
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.LEFT,
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.TOP,
              width: {
                size: 1000, // 두단: 450(10%크기),
                type: WidthType.DXA,
              },
            }),
            // 뜻
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    ...text.synonyms.map((word, indx) => {
                      const length = text?.synonyms?.length;
                      return new TextRun({
                        text:
                          indx !== length - 1
                            ? `${word.answer}, `
                            : word.answer,
                        bold: false,
                        size: fontSize,
                      });
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.LEFT,
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.CENTER,
              width: {
                size: 9000, // 두단: 4050시 40%크기
                type: WidthType.DXA,
              },
            }),
          ],
          height: {
            value: lineSpacing, // 높이 설정 (Twip 단위: 500 = 약 0.35인치)
            rule: HeightRule.ATLEAST, // 높이를 자동으로 설정
          },
        }),
      ],
      width: {
        size: MAX_WIDTH, // 4500
        type: WidthType.DXA,
      },
      layout: TableLayoutType.FIXED, // 테이블 레이아웃을 고정
    });
  }
  return result;
};

// E타입 텍스트 항목 생성
const createEtypeItem = (
  index,
  text,
  paperTab,
  fontSize,
  lineSpacing,
  vocaFirstWordShow,
) => {
  // '시험지', '정답표시 시험지'
  let result;
  if (
    paperTab === PAPER_TABS.TEST_PAPER ||
    paperTab === PAPER_TABS.ANSWER_TEST_PAPER
  ) {
    result = new Table({
      rows: [
        new TableRow({
          children: [
            // 번호 셀
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${index}. `, // 왼쪽 인덱스
                      bold: false,
                      size: fontSize,
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.LEFT, // 왼쪽 정렬
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.TOP, // 수직 정렬: 가운데
              width: {
                size: INDEX_WIDTH, // 450(10%크기)
                type: WidthType.DXA,
              },
            }),
            // 문제 셀
            new TableCell({
              children: [
                new Table({
                  rows: [
                    // 상단 영단어 + 한글해석 로우
                    new TableRow({
                      children: [
                        new TableCell({
                          children: [
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: strParse(text.question),
                                  bold: false,
                                  size: fontSize,
                                }),
                              ],
                              spacing: {
                                line: lineSpacing,
                              },
                              alignment: AlignmentType.LEFT,
                            }),
                          ],
                          borders: borderTransparent,
                          verticalAlign: VerticalAlign.TOP,
                          columnSpan: 3, // 열 3개를 병합
                          width: {
                            size: QUESTION_WIDTH, // 두단: 85%크기
                            type: WidthType.DXA,
                          },
                        }),
                      ],
                    }),
                    // 하단 ____ 뜻 로우
                    ...text.deriveds.map(word => {
                      return new TableRow({
                        children: [
                          // - 셀
                          new TableCell({
                            children: [
                              new Paragraph({
                                children: [
                                  new TextRun({
                                    text: `- `, // -
                                    bold: false,
                                    size: fontSize,
                                  }),
                                ],
                                spacing: {
                                  line: lineSpacing,
                                },
                              }),
                            ],
                            borders: borderTransparent,
                            verticalAlign: VerticalAlign.TOP,
                            width: {
                              size: QUESTION_WIDTH * 0.1, // 두단: 행 4050시의 10%인 405
                              type: WidthType.DXA,
                            },
                          }),
                          new TableCell({
                            children: [
                              new Table({
                                rows: [
                                  // 첫 번째 행: 정답 표시 + 밑줄 추가
                                  new TableRow({
                                    children: [
                                      new TableCell({
                                        children: vocaFirstWordShow
                                          ? [
                                              new Paragraph({
                                                children: [
                                                  new TextRun({
                                                    text:
                                                      paperTab ===
                                                      PAPER_TABS.TEST_PAPER
                                                        ? word.answer[0]
                                                        : word.answer, // 첫 번째 행의 정답 필드
                                                    bold: false,
                                                    size: fontSize,
                                                  }),
                                                ],
                                                spacing: {
                                                  line: lineSpacing,
                                                },
                                                alignment:
                                                  paperTab ===
                                                  PAPER_TABS.TEST_PAPER
                                                    ? AlignmentType.LEFT
                                                    : AlignmentType.CENTER,
                                              }),
                                            ]
                                          : [
                                              new Paragraph({
                                                children: [
                                                  new TextRun({
                                                    text:
                                                      paperTab ===
                                                      PAPER_TABS.TEST_PAPER
                                                        ? ''
                                                        : word.answer, // 첫 번째 행의 정답 필드
                                                    bold: false,
                                                    size: fontSize,
                                                  }),
                                                ],
                                                spacing: {
                                                  line: lineSpacing,
                                                },
                                                alignment: AlignmentType.CENTER,
                                              }),
                                            ],
                                        borders: borderBottom, // 밑줄 추가
                                        verticalAlign: VerticalAlign.BOTTOM,
                                        columnSpan: 2, // 모든 칼럼을 하나로 합침
                                      }),
                                    ],
                                  }),
                                  // 두 번째 행: 비어 있는 셀 (내용 없음)
                                  new TableRow({
                                    children: [
                                      new TableCell({
                                        children: [
                                          new Paragraph({
                                            children: [
                                              new TextRun({
                                                text: ' ', // 최소한의 내용 추가 (완전히 비우면 줄어들지 않을 수 있음)
                                                size: 1, // 폰트 최소 크기 설정 (1pt)
                                              }),
                                            ],
                                            spacing: {
                                              line: 100, // 줄 간격 최소화 (조정 가능)
                                            },
                                          }),
                                        ],
                                        borders: borderTransparent, // 테두리 없음
                                        verticalAlign: VerticalAlign.BOTTOM,
                                        columnSpan: 2, // 칼럼 병합 유지
                                      }),
                                    ],
                                    height: {
                                      value: 50, // 최소 높이 설정 (조정 가능, 기본 Twip 단위)
                                      rule: HeightRule.EXACT, // 정확한 높이로 설정하여 줄어들도록 강제
                                    },
                                  }),
                                ],
                                borders: borderTransparent, // 전체 테이블의 테두리 없음
                                width: {
                                  size: QUESTION_WIDTH * 0.45,
                                  type: WidthType.DXA,
                                },
                              }),
                            ],
                            borders: borderTransparent, // 외부 셀의 테두리 없음
                            width: {
                              size: QUESTION_WIDTH * 0.45,
                              type: WidthType.DXA,
                            },
                          }),
                          // 정답 셀
                          // new TableCell({
                          //   children: vocaFirstWordShow
                          //     ? [
                          //         new Paragraph({
                          //           children: [
                          //             new TextRun({
                          //               text:
                          //                 paperTab === PAPER_TABS.TEST_PAPER
                          //                   ? word.answer[0]
                          //                   : word.answer, // 정답 필드
                          //               bold: false,
                          //               size: fontSize,
                          //             }),
                          //           ],
                          //           spacing: {
                          //             line: lineSpacing,
                          //           },
                          //           alignment:
                          //             paperTab === PAPER_TABS.TEST_PAPER
                          //               ? AlignmentType.LEFT
                          //               : AlignmentType.CENTER,
                          //         }),
                          //       ]
                          //     : [
                          //         new Paragraph({
                          //           children: [
                          //             new TextRun({
                          //               text:
                          //                 paperTab === PAPER_TABS.TEST_PAPER
                          //                   ? ''
                          //                   : word.answer, // 정답 필드
                          //               bold: false,
                          //               size: fontSize,
                          //             }),
                          //           ],
                          //           spacing: {
                          //             line: lineSpacing,
                          //           },
                          //           alignment: AlignmentType.CENTER,
                          //         }),
                          //       ],
                          //   borders: borderBottom,
                          //   // borders: borderTransparent,
                          //   verticalAlign: VerticalAlign.TOP,
                          //   width: {
                          //     size: QUESTION_WIDTH * 0.45,
                          //     type: WidthType.DXA,
                          //   },
                          // }),
                          // 뜻 셀
                          new TableCell({
                            children: [
                              new Paragraph({
                                children: [
                                  new TextRun({
                                    text: `${word.question}`, // 뜻
                                    bold: false,
                                    size: fontSize,
                                  }),
                                ],
                                spacing: {
                                  line: lineSpacing,
                                },
                              }),
                            ],
                            borders: borderTransparent,
                            verticalAlign: VerticalAlign.TOP,
                            width: {
                              size: QUESTION_WIDTH * 0.45,
                              type: WidthType.DXA,
                            },
                          }),
                        ],
                        verticalAlign: VerticalAlign.CENTER,
                        width: {
                          size: QUESTION_WIDTH, // 두단: 85%크기,
                          type: WidthType.DXA,
                        },
                      });
                    }),
                  ],
                  borders: borderTransparent,
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.TOP, // 수직 정렬: 가운데
              width: {
                size: QUESTION_WIDTH, // (85%크기)
                type: WidthType.DXA,
              },
            }),
          ],
          height: {
            value: lineSpacing, // 높이 설정 (Twip 단위: 500 = 약 0.35인치)
            rule: HeightRule.ATLEAST, // 높이를 자동으로 설정
          },
        }),
      ],
      width: {
        size: MAX_WIDTH, // 4500
        type: WidthType.DXA,
      },
      layout: TableLayoutType.FIXED, // 테이블 레이아웃을 고정
    });
  }
  // '정답만' 시험지
  else if (paperTab === PAPER_TABS.ANSWER) {
    result = new Table({
      rows: [
        new TableRow({
          children: [
            // 번호 셀
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${index}. `, // 왼쪽 인덱스
                      bold: false,
                      size: fontSize,
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.LEFT,
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.TOP,
              width: {
                size: 1000, // 두단: 450(10%크기),
                type: WidthType.DXA,
              },
            }),
            // 뜻
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    ...text.deriveds.map((word, indx) => {
                      const length = text?.deriveds?.length;
                      return new TextRun({
                        text:
                          indx !== length - 1
                            ? `${word.answer}, `
                            : word.answer,
                        bold: false,
                        size: fontSize,
                      });
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.LEFT,
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.CENTER,
              width: {
                size: 9000, // 두단: 4050시 40%크기
                type: WidthType.DXA,
              },
            }),
          ],
          height: {
            value: lineSpacing, // 높이 설정 (Twip 단위: 500 = 약 0.35인치)
            rule: HeightRule.ATLEAST, // 높이를 자동으로 설정
          },
        }),
      ],
      width: {
        size: MAX_WIDTH, // 4500
        type: WidthType.DXA,
      },
      layout: TableLayoutType.FIXED, // 테이블 레이아웃을 고정
    });
  }
  return result;
};

// F타입 텍스트 항목 생성
const createFtypeItem = (
  index,
  text,
  paperTab,
  fontSize,
  lineSpacing,
  vocaFirstWordShow,
) => {
  // '시험지', '정답표시 시험지'
  let result;
  if (
    paperTab === PAPER_TABS.TEST_PAPER ||
    paperTab === PAPER_TABS.ANSWER_TEST_PAPER
  ) {
    result = new Table({
      rows: [
        new TableRow({
          children: [
            // 번호 셀 (10%)
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${index}.`,
                      bold: false,
                      size: fontSize,
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.LEFT,
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.TOP,
              width: {
                size: 1000, // 10% 너비
                type: WidthType.DXA,
              },
            }),
            // 좌측 문제 셀 (40%)
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    text.audioQuestionType === AUDIO_PAPER_TYPE.KOR
                      ? new TextRun({
                          text:
                            paperTab === PAPER_TABS.TEST_PAPER
                              ? ''
                              : strParse(text.meanings),
                          bold: false,
                          size: fontSize,
                        })
                      : new TextRun(
                          vocaFirstWordShow
                            ? {
                                text:
                                  paperTab === PAPER_TABS.TEST_PAPER
                                    ? text.answer[0]
                                    : text.answer,
                                bold: false,
                                size: fontSize,
                              }
                            : {
                                text:
                                  paperTab === PAPER_TABS.TEST_PAPER
                                    ? ''
                                    : text.answer,
                                bold: false,
                                size: fontSize,
                              },
                        ),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment:
                    paperTab === PAPER_TABS.TEST_PAPER && vocaFirstWordShow
                      ? AlignmentType.LEFT
                      : AlignmentType.CENTER,
                }),
              ],
              borders: borderBottom,
              verticalAlign: VerticalAlign.CENTER,
              width: {
                size: 4000, // 45% 너비
                type: WidthType.DXA,
              },
            }),
            // 여백 셀 (10%)
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: '',
                      bold: false,
                      size: fontSize,
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.CENTER,
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.CENTER,
              width: {
                size: 1000, // 10% 너비
                type: WidthType.DXA,
              },
            }),
            // 우측 문제 셀 (40%)
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    text.audioQuestionType === AUDIO_PAPER_TYPE.ENG_KOR
                      ? new TextRun({
                          text:
                            paperTab === PAPER_TABS.TEST_PAPER
                              ? ''
                              : strParse(text.meanings),
                          bold: false,
                          size: fontSize,
                        })
                      : new TextRun({
                          text: '',
                          bold: false,
                          size: fontSize,
                        }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.CENTER,
                }),
              ],
              borders:
                text.audioQuestionType === AUDIO_PAPER_TYPE.ENG_KOR
                  ? borderBottom
                  : borderTransparent,
              verticalAlign: VerticalAlign.CENTER,
              width: {
                size: 4000, // 45% 너비
                type: WidthType.DXA,
              },
            }),
          ],
          height: {
            value: lineSpacing, // 높이 설정 (Twip 단위: 500 = 약 0.35인치)
            rule: HeightRule.ATLEAST, // 최소 높이로 설정
          },
        }),
      ],
      width: {
        size: 100,
        type: WidthType.PERCENTAGE,
      },
      layout: TableLayoutType.FIXED, // 테이블 레이아웃을 고정
    });
  }
  // '정답만' 시험지
  else if (paperTab === PAPER_TABS.ANSWER) {
    result = new Table({
      rows: [
        new TableRow({
          children: [
            // 번호 셀 (10%)
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${index}.`,
                      bold: false,
                      size: fontSize,
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.LEFT,
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.TOP,
              width: {
                size: 1000, // 10% 너비
                type: WidthType.DXA,
              },
            }),
            // 뜻표시 셀 (90%)
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: text.answer,
                      bold: false,
                      size: fontSize,
                    }),
                    new TextRun({
                      text: '  ',
                      bold: false,
                      size: fontSize,
                    }),
                    new TextRun({
                      text: strParse(text.meanings),
                      bold: false,
                      size: fontSize,
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.LEFT,
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.CENTER,
              width: {
                size: 9000, // 45% 너비
                type: WidthType.DXA,
              },
            }),
          ],
          height: {
            value: lineSpacing, // 높이 설정 (Twip 단위: 500 = 약 0.35인치)
            rule: HeightRule.ATLEAST, // 최소 높이로 설정
          },
        }),
      ],
      width: {
        size: 100,
        type: WidthType.PERCENTAGE,
      },
      layout: TableLayoutType.FIXED, // 테이블 레이아웃을 고정
    });
  }
  return result;
};

// G타입 텍스트 항목 생성
const createGtypeItem = (
  index,
  text,
  paperTab,
  fontSize,
  lineSpacing,
  vocaFirstWordShow,
) => {
  // '시험지', '정답표시 시험지'
  let result;
  if (
    paperTab === PAPER_TABS.TEST_PAPER ||
    paperTab === PAPER_TABS.ANSWER_TEST_PAPER
  ) {
    result = new Table({
      rows: [
        new TableRow({
          children: [
            // 번호 셀
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${index}. `, // 왼쪽 인덱스
                      bold: false,
                      size: fontSize,
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.LEFT, // 왼쪽 정렬
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.TOP, // 수직 정렬: 가운데
              width: {
                size: MAX_WIDTH / 10, // 두단: 450(10%크기)
                type: WidthType.DXA,
              },
            }),
            // 문제 셀
            new TableCell({
              children: [
                new Table({
                  rows: [
                    // 상단 영영풀이 로우
                    new TableRow({
                      children: [
                        new TableCell({
                          children: [
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: strParse(text.question),
                                  bold: false,
                                  size: fontSize,
                                }),
                              ],
                              spacing: {
                                line: lineSpacing,
                              },
                              alignment: AlignmentType.LEFT,
                            }),
                          ],
                          borders: borderTransparent,
                          verticalAlign: VerticalAlign.TOP,
                          columnSpan: 3, // 열 3개를 병합
                          width: {
                            size: MAX_WIDTH - MAX_WIDTH / 10, // 두단: 4050(90%크기)
                            type: WidthType.DXA,
                          },
                        }),
                      ],
                    }),
                    // 하단 ____ 뜻 로우
                    new TableRow({
                      children: [
                        // 정답 셀
                        new TableCell({
                          children: [
                            new Paragraph({
                              children: vocaFirstWordShow
                                ? [
                                    new TextRun({
                                      text:
                                        paperTab === PAPER_TABS.TEST_PAPER
                                          ? text.answer[0]
                                          : text.answer, // 정답 필드
                                      bold: false,
                                      size: fontSize,
                                    }),
                                  ]
                                : [
                                    new TextRun({
                                      text:
                                        paperTab === PAPER_TABS.TEST_PAPER
                                          ? ''
                                          : text.answer, // 정답 필드
                                      bold: false,
                                      size: fontSize,
                                    }),
                                  ],
                              spacing: {
                                line: lineSpacing,
                              },
                              alignment:
                                paperTab === PAPER_TABS.TEST_PAPER &&
                                vocaFirstWordShow
                                  ? AlignmentType.LEFT
                                  : AlignmentType.CENTER,
                            }),
                          ],
                          borders: borderBottom,
                          verticalAlign: VerticalAlign.CENTER,
                          width: {
                            size: (MAX_WIDTH - MAX_WIDTH / 10) * 0.5, // 두단: 4050시 50%크기
                            type: WidthType.DXA,
                          },
                        }),
                        new TableCell({
                          children: [
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: '',
                                  bold: false,
                                  size: fontSize,
                                }),
                              ],
                              spacing: {
                                line: lineSpacing,
                              },
                              alignment: AlignmentType.LEFT,
                            }),
                          ],
                          borders: borderTransparent,
                          verticalAlign: VerticalAlign.CENTER,
                          width: {
                            size: (MAX_WIDTH - MAX_WIDTH / 10) * 0.5, // 두단: 4050시 50%크기
                            type: WidthType.DXA,
                          },
                        }),
                      ],
                      verticalAlign: VerticalAlign.CENTER,
                      width: {
                        size: MAX_WIDTH - MAX_WIDTH / 10, // 두단: 4050(90%크기),
                        type: WidthType.DXA,
                      },
                    }),
                  ],
                  borders: borderTransparent,
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.TOP, // 수직 정렬: 가운데
              width: {
                size: MAX_WIDTH - MAX_WIDTH / 10, // 두단: 4050(90%크기)
                type: WidthType.DXA,
              },
            }),
          ],
          height: {
            value: lineSpacing, // 높이 설정 (Twip 단위: 500 = 약 0.35인치)
            rule: HeightRule.ATLEAST, // 높이를 자동으로 설정
          },
        }),
      ],
      width: {
        size: MAX_WIDTH, // 4500
        type: WidthType.DXA,
      },
      layout: TableLayoutType.FIXED, // 테이블 레이아웃을 고정
    });
  }
  // '정답만' 시험지
  else if (paperTab === PAPER_TABS.ANSWER) {
    result = new Table({
      rows: [
        new TableRow({
          children: [
            // 번호 셀
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${index}. `, // 왼쪽 인덱스
                      bold: false,
                      size: fontSize,
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.LEFT,
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.TOP,
              width: {
                size: 1000, // 두단: 450(10%크기),
                type: WidthType.DXA,
              },
            }),
            // 뜻
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: text.answer,
                      bold: false,
                      size: fontSize,
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.LEFT,
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.CENTER,
              width: {
                size: 9000, // 두단: 4050시 40%크기
                type: WidthType.DXA,
              },
            }),
          ],
          height: {
            value: lineSpacing, // 높이 설정 (Twip 단위: 500 = 약 0.35인치)
            rule: HeightRule.ATLEAST, // 높이를 자동으로 설정
          },
        }),
      ],
      width: {
        size: MAX_WIDTH, // 4500
        type: WidthType.DXA,
      },
      layout: TableLayoutType.FIXED, // 테이블 레이아웃을 고정
    });
  }
  return result;
};

// H타입 텍스트 항목 생성
const createHtypeItem = (index, text, paperTab, fontSize, lineSpacing) => {
  // '시험지', '정답표시 시험지'
  let result;
  if (
    paperTab === PAPER_TABS.TEST_PAPER ||
    paperTab === PAPER_TABS.ANSWER_TEST_PAPER
  ) {
    result = new Table({
      rows: [
        new TableRow({
          children: [
            // 번호 셀
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${index}. `, // 왼쪽 인덱스
                      bold: false,
                      size: fontSize,
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.LEFT, // 왼쪽 정렬
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.TOP, // 수직 정렬: 가운데
              width: {
                size: MAX_WIDTH / 10, // 두단: 450(10%크기)
                type: WidthType.DXA,
              },
            }),
            // 문제 셀
            new TableCell({
              children: [
                new Table({
                  rows: [
                    // 상단 영영풀이 로우
                    new TableRow({
                      children: [
                        new TableCell({
                          children: [
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: strParse(text.question),
                                  bold: false,
                                  size: fontSize,
                                }),
                              ],
                              spacing: {
                                line: lineSpacing,
                              },
                              alignment: AlignmentType.LEFT,
                            }),
                          ],
                          borders: borderTransparent,
                          verticalAlign: VerticalAlign.TOP,
                          columnSpan: 3, // 열 3개를 병합
                          width: {
                            size: MAX_WIDTH - MAX_WIDTH / 10, // 두단: 4050(90%크기)
                            type: WidthType.DXA,
                          },
                        }),
                      ],
                    }),
                    // 하단 객관식 로우 반복
                    ...text.options.map((option, oIdx) => {
                      return new TableRow({
                        children: [
                          // 보기숫자
                          new TableCell({
                            children: [
                              new Paragraph({
                                children: [
                                  new TextRun({
                                    text: `${String.fromCharCode(9312 + oIdx)}`, // ①, ②, ③..
                                    bold: false,
                                    size: fontSize,
                                  }),
                                ],
                                spacing: {
                                  line: lineSpacing,
                                },
                                alignment: AlignmentType.LEFT,
                              }),
                            ],
                            borders: borderTransparent,
                            verticalAlign: VerticalAlign.TOP,
                            width: {
                              size: (MAX_WIDTH - MAX_WIDTH / 10) * 0.1, // 두단: 4050시 50%크기
                              type: WidthType.DXA,
                            },
                          }),
                          // 정답 셀
                          new TableCell({
                            children: [
                              new Paragraph({
                                children: [
                                  new TextRun({
                                    text: option,
                                    bold:
                                      paperTab ===
                                        PAPER_TABS.ANSWER_TEST_PAPER &&
                                      text.answer === oIdx + 1
                                        ? true
                                        : false,
                                    underline:
                                      paperTab ===
                                        PAPER_TABS.ANSWER_TEST_PAPER &&
                                      text.answer === oIdx + 1
                                        ? {}
                                        : { type: 'none' },
                                    size: fontSize,
                                  }),
                                ],
                                spacing: {
                                  line: lineSpacing,
                                },
                                alignment: AlignmentType.LEFT,
                              }),
                            ],
                            borders: borderTransparent,
                            verticalAlign: VerticalAlign.CENTER,
                            width: {
                              size: (MAX_WIDTH - MAX_WIDTH / 10) * 0.9, // 두단: 4050시 50%크기
                              type: WidthType.DXA,
                            },
                          }),
                        ],
                        verticalAlign: VerticalAlign.CENTER,
                        width: {
                          size: MAX_WIDTH - MAX_WIDTH / 10, // 두단: 4050(90%크기),
                          type: WidthType.DXA,
                        },
                      });
                    }),
                  ],
                  borders: borderTransparent,
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.TOP, // 수직 정렬: 가운데
              width: {
                size: MAX_WIDTH - MAX_WIDTH / 10, // 두단: 4050(90%크기)
                type: WidthType.DXA,
              },
            }),
          ],
          height: {
            value: lineSpacing, // 높이 설정 (Twip 단위: 500 = 약 0.35인치)
            rule: HeightRule.ATLEAST, // 높이를 자동으로 설정
          },
        }),
      ],
      width: {
        size: MAX_WIDTH, // 4500
        type: WidthType.DXA,
      },
      layout: TableLayoutType.FIXED, // 테이블 레이아웃을 고정
    });
  }
  // '정답만' 시험지
  else if (paperTab === PAPER_TABS.ANSWER) {
    result = new Table({
      rows: [
        new TableRow({
          children: [
            // 번호 셀
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${index}. `, // 왼쪽 인덱스
                      bold: false,
                      size: fontSize,
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.LEFT,
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.TOP,
              width: {
                size: 1000, // 두단: 450(10%크기),
                type: WidthType.DXA,
              },
            }),
            // 뜻
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      // text: text?.options[text?.answer - 1],
                      text: `${String.fromCharCode(9312 + text.answer - 1)}`,
                      bold: false,
                      size: fontSize,
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.LEFT,
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.CENTER,
              width: {
                size: 9000, // 두단: 4050시 40%크기
                type: WidthType.DXA,
              },
            }),
          ],
          height: {
            value: lineSpacing, // 높이 설정 (Twip 단위: 500 = 약 0.35인치)
            rule: HeightRule.ATLEAST, // 높이를 자동으로 설정
          },
        }),
      ],
      width: {
        size: MAX_WIDTH, // 4500
        type: WidthType.DXA,
      },
      layout: TableLayoutType.FIXED, // 테이블 레이아웃을 고정
    });
  }
  return result;
};

// I타입 텍스트 항목 생성
const createItypeItem = (index, text, paperTab, fontSize, lineSpacing) => {
  // '시험지', '정답표시 시험지'
  let result;
  if (
    paperTab === PAPER_TABS.TEST_PAPER ||
    paperTab === PAPER_TABS.ANSWER_TEST_PAPER
  ) {
    result = new Table({
      rows: [
        new TableRow({
          children: [
            // 번호 셀
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${index}. `, // 왼쪽 인덱스
                      bold: false,
                      size: fontSize,
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.LEFT, // 왼쪽 정렬
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.TOP, // 수직 정렬: 가운데
              width: {
                size: MAX_WIDTH / 10, // 두단: 450(10%크기)
                type: WidthType.DXA,
              },
            }),
            // 문제 셀
            new TableCell({
              children: [
                new Table({
                  rows: [
                    // 상단 영영풀이 로우
                    new TableRow({
                      children: [
                        new TableCell({
                          children: [
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: strParse(text.question),
                                  bold: false,
                                  size: fontSize,
                                }),
                              ],
                              spacing: {
                                line: lineSpacing,
                              },
                              alignment: AlignmentType.LEFT,
                            }),
                          ],
                          borders: borderTransparent,
                          verticalAlign: VerticalAlign.TOP,
                          columnSpan: 2, // 열 3개를 병합
                          width: {
                            size: MAX_WIDTH - MAX_WIDTH / 10, // 두단: 4050(90%크기)
                            type: WidthType.DXA,
                          },
                        }),
                      ],
                    }),
                    // 하단 객관식 로우 반복
                    ...text.options.map((option, oIdx) => {
                      return new TableRow({
                        children: [
                          // 보기숫자
                          new TableCell({
                            children: [
                              new Paragraph({
                                children: [
                                  new TextRun({
                                    text: `${String.fromCharCode(9312 + oIdx)}`, // ①, ②, ③..
                                    bold: false,
                                    size: fontSize,
                                  }),
                                ],
                                spacing: {
                                  line: lineSpacing,
                                },
                                alignment: AlignmentType.LEFT,
                              }),
                            ],
                            borders: borderTransparent,
                            verticalAlign: VerticalAlign.TOP,
                            width: {
                              size: (MAX_WIDTH - MAX_WIDTH / 10) * 0.1, // 두단: 4050시 50%크기
                              type: WidthType.DXA,
                            },
                          }),
                          // 정답 셀
                          new TableCell({
                            children: [
                              new Paragraph({
                                children: [
                                  new TextRun({
                                    text: option,
                                    bold:
                                      paperTab ===
                                        PAPER_TABS.ANSWER_TEST_PAPER &&
                                      text.answer === oIdx + 1
                                        ? true
                                        : false,
                                    underline:
                                      paperTab ===
                                        PAPER_TABS.ANSWER_TEST_PAPER &&
                                      text.answer === oIdx + 1
                                        ? {}
                                        : { type: 'none' },
                                    size: fontSize,
                                  }),
                                ],
                                spacing: {
                                  line: lineSpacing,
                                },
                                alignment: AlignmentType.LEFT,
                              }),
                            ],
                            borders: borderTransparent,
                            verticalAlign: VerticalAlign.CENTER,
                            width: {
                              size: (MAX_WIDTH - MAX_WIDTH / 10) * 0.9, // 두단: 4050시 50%크기
                              type: WidthType.DXA,
                            },
                          }),
                        ],
                        verticalAlign: VerticalAlign.CENTER,
                        width: {
                          size: MAX_WIDTH - MAX_WIDTH / 10, // 두단: 4050(90%크기),
                          type: WidthType.DXA,
                        },
                      });
                    }),
                  ],
                  borders: borderTransparent,
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.TOP, // 수직 정렬: 가운데
              width: {
                size: MAX_WIDTH - MAX_WIDTH / 10, // 두단: 4050(90%크기)
                type: WidthType.DXA,
              },
            }),
          ],
          height: {
            value: lineSpacing, // 높이 설정 (Twip 단위: 500 = 약 0.35인치)
            rule: HeightRule.ATLEAST, // 높이를 자동으로 설정
          },
        }),
      ],
      width: {
        size: MAX_WIDTH, // 4500
        type: WidthType.DXA,
      },
      layout: TableLayoutType.FIXED, // 테이블 레이아웃을 고정
    });
  }
  // '정답만' 시험지
  else if (paperTab === PAPER_TABS.ANSWER) {
    result = new Table({
      rows: [
        new TableRow({
          children: [
            // 번호 셀
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${index}. `, // 왼쪽 인덱스
                      bold: false,
                      size: fontSize,
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.LEFT,
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.TOP,
              width: {
                size: 1000, // 두단: 450(10%크기),
                type: WidthType.DXA,
              },
            }),
            // 뜻
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      // text: text?.options[text?.answer - 1],
                      text: `${String.fromCharCode(9312 + text.answer - 1)}`,
                      bold: false,
                      size: fontSize,
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.LEFT,
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.CENTER,
              width: {
                size: 9000, // 두단: 4050시 40%크기
                type: WidthType.DXA,
              },
            }),
          ],
          height: {
            value: lineSpacing, // 높이 설정 (Twip 단위: 500 = 약 0.35인치)
            rule: HeightRule.ATLEAST, // 높이를 자동으로 설정
          },
        }),
      ],
      width: {
        size: MAX_WIDTH, // 4500
        type: WidthType.DXA,
      },
      layout: TableLayoutType.FIXED, // 테이블 레이아웃을 고정
    });
  }
  return result;
};

// J타입 텍스트 항목 생성
const createJtypeItem = (
  index,
  text,
  paperTab,
  fontSize,
  lineSpacing,
  examKorShow,
) => {
  // '시험지', '정답표시 시험지'
  let result;
  let answerTextIndex = null; // 영문 정답단어 식별용
  if (
    paperTab === PAPER_TABS.TEST_PAPER ||
    paperTab === PAPER_TABS.ANSWER_TEST_PAPER
  ) {
    result = new Table({
      rows: [
        new TableRow({
          children: [
            // 번호 셀
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${index}. `, // 왼쪽 인덱스
                      bold: false,
                      size: fontSize,
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.LEFT, // 왼쪽 정렬
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.TOP, // 수직 정렬: 가운데
              width: {
                size: MAX_WIDTH / 10, // 두단: 450(10%크기)
                type: WidthType.DXA,
              },
            }),
            // 문제 셀
            new TableCell({
              children: [
                new Table({
                  rows: [
                    // 상단 영문 로우
                    new TableRow({
                      children: [
                        new TableCell({
                          children: [
                            new Paragraph({
                              children: [
                                ...text.sentence
                                  .split(' ')
                                  .map((text, tIndex) => {
                                    let isAnswerText = false;
                                    if (
                                      text.startsWith('{') &&
                                      (text.endsWith('}') ||
                                        text.endsWith('}.'))
                                    ) {
                                      isAnswerText = true;
                                      answerTextIndex = tIndex;
                                    }
                                    let resultText = '';
                                    if (isAnswerText) {
                                      resultText =
                                        answerTextIndex !== null
                                          ? `${text?.replace(/[{}]/g, '')?.replace(/\^/g, ' ')}`
                                          : `${text?.replace(/[{}]/g, '')?.replace(/\^/g, ' ')} `;
                                    } else {
                                      resultText =
                                        answerTextIndex !== null &&
                                        tIndex === answerTextIndex + 1
                                          ? ` ${text} `
                                          : `${text} `;
                                    }
                                    return new TextRun({
                                      text: resultText,
                                      bold: isAnswerText ? true : false,
                                      underline: isAnswerText
                                        ? {}
                                        : { type: 'none' },
                                      size: fontSize,
                                    });
                                  }),
                              ],
                              spacing: {
                                line: lineSpacing,
                              },
                              alignment: AlignmentType.LEFT,
                            }),
                          ],
                          borders: borderTransparent,
                          verticalAlign: VerticalAlign.TOP,
                          columnSpan: 2, // 열 3개를 병합
                          width: {
                            size: MAX_WIDTH - MAX_WIDTH / 10, // 두단: 4050(90%크기)
                            type: WidthType.DXA,
                          },
                        }),
                      ],
                    }),
                    // 중단 국문 해석 로우

                    ...(!examKorShow
                      ? []
                      : [
                          new TableRow({
                            children: [
                              new TableCell({
                                children: [
                                  new Paragraph({
                                    children: [
                                      new TextRun({
                                        text: `(${text?.translation ?? ''})`,
                                        bold: false,
                                        size: fontSize,
                                      }),
                                    ],
                                    spacing: {
                                      line: lineSpacing,
                                    },
                                    alignment: AlignmentType.LEFT,
                                  }),
                                ],
                                borders: borderTransparent,
                                verticalAlign: VerticalAlign.TOP,
                                columnSpan: 2, // 열 3개를 병합
                                width: {
                                  size: MAX_WIDTH - MAX_WIDTH / 10, // 두단: 4050(90%크기)
                                  type: WidthType.DXA,
                                },
                              }),
                            ],
                          }),
                        ]),
                    // 하단 객관식 로우 반복
                    ...text.options.map((option, oIdx) => {
                      return new TableRow({
                        children: [
                          // 보기숫자
                          new TableCell({
                            children: [
                              new Paragraph({
                                children: [
                                  new TextRun({
                                    text: `${String.fromCharCode(9312 + oIdx)}`, // ①, ②, ③..
                                    bold: false,
                                    size: fontSize,
                                  }),
                                ],
                                spacing: {
                                  line: lineSpacing,
                                },
                                alignment: AlignmentType.LEFT,
                              }),
                            ],
                            borders: borderTransparent,
                            verticalAlign: VerticalAlign.TOP,
                            width: {
                              size: (MAX_WIDTH - MAX_WIDTH / 10) * 0.1, // 두단: 4050시 50%크기
                              type: WidthType.DXA,
                            },
                          }),
                          // 정답 셀
                          new TableCell({
                            children: [
                              new Paragraph({
                                children: [
                                  new TextRun({
                                    text: option,
                                    bold:
                                      paperTab ===
                                        PAPER_TABS.ANSWER_TEST_PAPER &&
                                      text.answer === oIdx + 1
                                        ? true
                                        : false,
                                    underline:
                                      paperTab ===
                                        PAPER_TABS.ANSWER_TEST_PAPER &&
                                      text.answer === oIdx + 1
                                        ? {}
                                        : { type: 'none' },
                                    size: fontSize,
                                  }),
                                ],
                                spacing: {
                                  line: lineSpacing,
                                },
                                alignment: AlignmentType.LEFT,
                              }),
                            ],
                            borders: borderTransparent,
                            verticalAlign: VerticalAlign.CENTER,
                            width: {
                              size: (MAX_WIDTH - MAX_WIDTH / 10) * 0.9, // 두단: 4050시 50%크기
                              type: WidthType.DXA,
                            },
                          }),
                        ],
                        verticalAlign: VerticalAlign.CENTER,
                        width: {
                          size: MAX_WIDTH - MAX_WIDTH / 10, // 두단: 4050(90%크기),
                          type: WidthType.DXA,
                        },
                      });
                    }),
                  ],
                  borders: borderTransparent,
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.TOP, // 수직 정렬: 가운데
              width: {
                size: MAX_WIDTH - MAX_WIDTH / 10, // 두단: 4050(90%크기)
                type: WidthType.DXA,
              },
            }),
          ],
          height: {
            value: lineSpacing, // 높이 설정 (Twip 단위: 500 = 약 0.35인치)
            rule: HeightRule.ATLEAST, // 높이를 자동으로 설정
          },
        }),
      ],
      width: {
        size: MAX_WIDTH, // 4500
        type: WidthType.DXA,
      },
      layout: TableLayoutType.FIXED, // 테이블 레이아웃을 고정
    });
  }
  // '정답만' 시험지
  else if (paperTab === PAPER_TABS.ANSWER) {
    result = new Table({
      rows: [
        new TableRow({
          children: [
            // 번호 셀
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${index}. `, // 왼쪽 인덱스
                      bold: false,
                      size: fontSize,
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.LEFT,
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.TOP,
              width: {
                size: 1000, // 두단: 450(10%크기),
                type: WidthType.DXA,
              },
            }),
            // 뜻
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      // text: text?.options[text?.answer - 1],
                      text: `${String.fromCharCode(9312 + text.answer - 1)}`,
                      bold: false,
                      size: fontSize,
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.LEFT,
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.CENTER,
              width: {
                size: 9000, // 두단: 4050시 40%크기
                type: WidthType.DXA,
              },
            }),
          ],
          height: {
            value: lineSpacing, // 높이 설정 (Twip 단위: 500 = 약 0.35인치)
            rule: HeightRule.ATLEAST, // 높이를 자동으로 설정
          },
        }),
      ],
      width: {
        size: MAX_WIDTH, // 4500
        type: WidthType.DXA,
      },
      layout: TableLayoutType.FIXED, // 테이블 레이아웃을 고정
    });
  }
  return result;
};

// K타입 텍스트 항목 생성
const createKtypeItem = (
  index,
  text,
  paperTab,
  fontSize,
  lineSpacing,
  examKorShow,
) => {
  // '시험지', '정답표시 시험지'
  let result;
  if (
    paperTab === PAPER_TABS.TEST_PAPER ||
    paperTab === PAPER_TABS.ANSWER_TEST_PAPER
  ) {
    result = new Table({
      rows: [
        new TableRow({
          children: [
            // 번호 셀
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${index}. `, // 왼쪽 인덱스
                      bold: false,
                      size: fontSize,
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.LEFT, // 왼쪽 정렬
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.TOP, // 수직 정렬: 가운데
              width: {
                size: MAX_WIDTH / 10, // 두단: 450(10%크기)
                type: WidthType.DXA,
              },
            }),
            // 문제 셀
            new TableCell({
              children: [
                new Table({
                  rows: [
                    // 상단 영문 로우
                    new TableRow({
                      children: [
                        new TableCell({
                          children: [
                            new Paragraph({
                              children: [
                                ...text.sentence.split(' ').map(text => {
                                  let isAnswerText = false;
                                  if (
                                    text.startsWith('{') &&
                                    (text.endsWith('}') ||
                                      text.endsWith('}.') ||
                                      text.endsWith('},'))
                                  ) {
                                    isAnswerText = true;
                                  }
                                  let resultText = '';
                                  if (isAnswerText) {
                                    // resultText = '_'.repeat(text?.length) + ' ';
                                    resultText = '___________________ ';
                                  } else {
                                    resultText = `${text} `;
                                  }
                                  return new TextRun({
                                    text: resultText,
                                    bold: false,
                                    size: fontSize,
                                  });
                                }),
                              ],
                              spacing: {
                                line: lineSpacing,
                              },
                              alignment: AlignmentType.LEFT,
                            }),
                          ],
                          borders: borderTransparent,
                          verticalAlign: VerticalAlign.TOP,
                          columnSpan: 2, // 열 3개를 병합
                          width: {
                            size: MAX_WIDTH - MAX_WIDTH / 10, // 두단: 4050(90%크기)
                            type: WidthType.DXA,
                          },
                        }),
                      ],
                    }),
                    // 중단 국문 해석 로우
                    ...(!examKorShow
                      ? []
                      : [
                          new TableRow({
                            children: [
                              new TableCell({
                                children: [
                                  new Paragraph({
                                    children: [
                                      new TextRun({
                                        text: `(${text?.translation ?? ''})`,
                                        bold: false,
                                        size: fontSize,
                                      }),
                                    ],
                                    spacing: {
                                      line: lineSpacing,
                                    },
                                    alignment: AlignmentType.LEFT,
                                  }),
                                ],
                                borders: borderTransparent,
                                verticalAlign: VerticalAlign.TOP,
                                columnSpan: 2, // 열 3개를 병합
                                width: {
                                  size: MAX_WIDTH - MAX_WIDTH / 10, // 두단: 4050(90%크기)
                                  type: WidthType.DXA,
                                },
                              }),
                            ],
                          }),
                        ]),
                    // 하단 객관식 로우 반복
                    ...text.options.map((option, oIdx) => {
                      return new TableRow({
                        children: [
                          // 보기숫자
                          new TableCell({
                            children: [
                              new Paragraph({
                                children: [
                                  new TextRun({
                                    text: `${String.fromCharCode(9312 + oIdx)}`, // ①, ②, ③..
                                    bold: false,
                                    size: fontSize,
                                  }),
                                ],
                                spacing: {
                                  line: lineSpacing,
                                },
                                alignment: AlignmentType.LEFT,
                              }),
                            ],
                            borders: borderTransparent,
                            verticalAlign: VerticalAlign.TOP,
                            width: {
                              size: (MAX_WIDTH - MAX_WIDTH / 10) * 0.1, // 두단: 4050시 50%크기
                              type: WidthType.DXA,
                            },
                          }),
                          // 정답 셀
                          new TableCell({
                            children: [
                              new Paragraph({
                                children: [
                                  new TextRun({
                                    text: option,
                                    bold:
                                      paperTab ===
                                        PAPER_TABS.ANSWER_TEST_PAPER &&
                                      text.answer === oIdx + 1
                                        ? true
                                        : false,
                                    underline:
                                      paperTab ===
                                        PAPER_TABS.ANSWER_TEST_PAPER &&
                                      text.answer === oIdx + 1
                                        ? {}
                                        : { type: 'none' },
                                    size: fontSize,
                                  }),
                                ],
                                spacing: {
                                  line: lineSpacing,
                                },
                                alignment: AlignmentType.LEFT,
                              }),
                            ],
                            borders: borderTransparent,
                            verticalAlign: VerticalAlign.CENTER,
                            width: {
                              size: (MAX_WIDTH - MAX_WIDTH / 10) * 0.9, // 두단: 4050시 50%크기
                              type: WidthType.DXA,
                            },
                          }),
                        ],
                        verticalAlign: VerticalAlign.CENTER,
                        width: {
                          size: MAX_WIDTH - MAX_WIDTH / 10, // 두단: 4050(90%크기),
                          type: WidthType.DXA,
                        },
                      });
                    }),
                  ],
                  borders: borderTransparent,
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.TOP, // 수직 정렬: 가운데
              width: {
                size: MAX_WIDTH - MAX_WIDTH / 10, // 두단: 4050(90%크기)
                type: WidthType.DXA,
              },
            }),
          ],
          height: {
            value: lineSpacing, // 높이 설정 (Twip 단위: 500 = 약 0.35인치)
            rule: HeightRule.ATLEAST, // 높이를 자동으로 설정
          },
        }),
      ],
      width: {
        size: MAX_WIDTH, // 4500
        type: WidthType.DXA,
      },
      layout: TableLayoutType.FIXED, // 테이블 레이아웃을 고정
    });
  }
  // '정답만' 시험지
  else if (paperTab === PAPER_TABS.ANSWER) {
    result = new Table({
      rows: [
        new TableRow({
          children: [
            // 번호 셀
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${index}. `, // 왼쪽 인덱스
                      bold: false,
                      size: fontSize,
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.LEFT,
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.TOP,
              width: {
                size: 1000, // 두단: 450(10%크기),
                type: WidthType.DXA,
              },
            }),
            // 뜻
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      // text: text?.options[text?.answer - 1],
                      text: `${String.fromCharCode(9312 + text.answer - 1)}`,
                      bold: false,
                      size: fontSize,
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.LEFT,
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.CENTER,
              width: {
                size: 9000, // 두단: 4050시 40%크기
                type: WidthType.DXA,
              },
            }),
          ],
          height: {
            value: lineSpacing, // 높이 설정 (Twip 단위: 500 = 약 0.35인치)
            rule: HeightRule.ATLEAST, // 높이를 자동으로 설정
          },
        }),
      ],
      width: {
        size: MAX_WIDTH, // 4500
        type: WidthType.DXA,
      },
      layout: TableLayoutType.FIXED, // 테이블 레이아웃을 고정
    });
  }
  return result;
};

// M타입 텍스트 항목 생성
const createMtypeItem = (index, text, paperTab, fontSize, lineSpacing) => {
  // '시험지', '정답표시 시험지'
  let result;
  if (
    paperTab === PAPER_TABS.TEST_PAPER ||
    paperTab === PAPER_TABS.ANSWER_TEST_PAPER
  ) {
    result = new Table({
      rows: [
        new TableRow({
          children: [
            // 번호 셀
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${index}. `, // 왼쪽 인덱스
                      bold: false,
                      size: fontSize,
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.LEFT, // 왼쪽 정렬
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.TOP, // 수직 정렬: 가운데
              width: {
                size: MAX_WIDTH / 10, // 두단: 450(10%크기)
                type: WidthType.DXA,
              },
            }),
            // 문제 셀
            new TableCell({
              children: [
                new Table({
                  rows: [
                    // 상단 영어문제 로우
                    new TableRow({
                      children: [
                        new TableCell({
                          children: [
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: text.question,
                                  bold: false,
                                  size: fontSize,
                                }),
                              ],
                              spacing: {
                                line: lineSpacing,
                              },
                              alignment: AlignmentType.LEFT,
                            }),
                          ],
                          borders: borderTransparent,
                          verticalAlign: VerticalAlign.TOP,
                          columnSpan: 2, // 열 3개를 병합
                          width: {
                            size: MAX_WIDTH - MAX_WIDTH / 10, // 두단: 4050(90%크기)
                            type: WidthType.DXA,
                          },
                        }),
                      ],
                    }),
                    // 하단 객관식 로우 반복
                    ...text.options.map((option, oIdx) => {
                      return new TableRow({
                        children: [
                          // 보기숫자
                          new TableCell({
                            children: [
                              new Paragraph({
                                children: [
                                  new TextRun({
                                    text: `${String.fromCharCode(9312 + oIdx)}`, // ①, ②, ③..
                                    bold: false,
                                    size: fontSize,
                                  }),
                                ],
                                spacing: {
                                  line: lineSpacing,
                                },
                                alignment: AlignmentType.LEFT,
                              }),
                            ],
                            borders: borderTransparent,
                            verticalAlign: VerticalAlign.TOP,
                            width: {
                              size: (MAX_WIDTH - MAX_WIDTH / 10) * 0.1, // 두단: 4050시 50%크기
                              type: WidthType.DXA,
                            },
                          }),
                          // 정답 셀
                          new TableCell({
                            children: [
                              new Paragraph({
                                children: [
                                  new TextRun({
                                    text: option,
                                    bold:
                                      paperTab ===
                                        PAPER_TABS.ANSWER_TEST_PAPER &&
                                      text.answer === oIdx + 1
                                        ? true
                                        : false,
                                    underline:
                                      paperTab ===
                                        PAPER_TABS.ANSWER_TEST_PAPER &&
                                      text.answer === oIdx + 1
                                        ? {}
                                        : { type: 'none' },
                                    size: fontSize,
                                  }),
                                ],
                                spacing: {
                                  line: lineSpacing,
                                },
                                alignment: AlignmentType.LEFT,
                              }),
                            ],
                            borders: borderTransparent,
                            verticalAlign: VerticalAlign.CENTER,
                            width: {
                              size: (MAX_WIDTH - MAX_WIDTH / 10) * 0.9, // 두단: 4050시 50%크기
                              type: WidthType.DXA,
                            },
                          }),
                        ],
                        verticalAlign: VerticalAlign.CENTER,
                        width: {
                          size: MAX_WIDTH - MAX_WIDTH / 10, // 두단: 4050(90%크기),
                          type: WidthType.DXA,
                        },
                      });
                    }),
                  ],
                  borders: borderTransparent,
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.TOP, // 수직 정렬: 가운데
              width: {
                size: MAX_WIDTH - MAX_WIDTH / 10, // 두단: 4050(90%크기)
                type: WidthType.DXA,
              },
            }),
          ],
          height: {
            value: lineSpacing, // 높이 설정 (Twip 단위: 500 = 약 0.35인치)
            rule: HeightRule.ATLEAST, // 높이를 자동으로 설정
          },
        }),
      ],
      width: {
        size: MAX_WIDTH, // 4500
        type: WidthType.DXA,
      },
      layout: TableLayoutType.FIXED, // 테이블 레이아웃을 고정
    });
  }
  // '정답만' 시험지
  else if (paperTab === PAPER_TABS.ANSWER) {
    result = new Table({
      rows: [
        new TableRow({
          children: [
            // 번호 셀
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${index}. `, // 왼쪽 인덱스
                      bold: false,
                      size: fontSize,
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.LEFT,
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.TOP,
              width: {
                size: 1000, // 두단: 450(10%크기),
                type: WidthType.DXA,
              },
            }),
            // 뜻
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      // text: text?.options[text?.answer - 1],
                      text: `${String.fromCharCode(9312 + text.answer - 1)}`,
                      bold: false,
                      size: fontSize,
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.LEFT,
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.CENTER,
              width: {
                size: 9000, // 두단: 4050시 40%크기
                type: WidthType.DXA,
              },
            }),
          ],
          height: {
            value: lineSpacing, // 높이 설정 (Twip 단위: 500 = 약 0.35인치)
            rule: HeightRule.ATLEAST, // 높이를 자동으로 설정
          },
        }),
      ],
      width: {
        size: MAX_WIDTH, // 4500
        type: WidthType.DXA,
      },
      layout: TableLayoutType.FIXED, // 테이블 레이아웃을 고정
    });
  }
  return result;
};
// L타입 텍스트 항목 생성
const createLtypeItem = (index, text, paperTab, fontSize, lineSpacing) => {
  // '시험지', '정답표시 시험지'
  let result;
  if (
    paperTab === PAPER_TABS.TEST_PAPER ||
    paperTab === PAPER_TABS.ANSWER_TEST_PAPER
  ) {
    result = new Table({
      rows: [
        new TableRow({
          children: [
            // 번호 셀
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${index}. `, // 왼쪽 인덱스
                      bold: false,
                      size: fontSize,
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.LEFT, // 왼쪽 정렬
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.TOP, // 수직 정렬: 가운데
              width: {
                size: MAX_WIDTH / 10, // 두단: 450(10%크기)
                type: WidthType.DXA,
              },
            }),
            // 문제 셀
            new TableCell({
              children: [
                new Table({
                  rows: [
                    // 상단 영단어 로우
                    new TableRow({
                      children: [
                        new TableCell({
                          children: [
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: strParse(text.question),
                                  bold: false,
                                  size: fontSize,
                                }),
                              ],
                              spacing: {
                                line: lineSpacing,
                              },
                              alignment: AlignmentType.LEFT,
                            }),
                          ],
                          borders: borderTransparent,
                          verticalAlign: VerticalAlign.TOP,
                          columnSpan: 2, // 열 3개를 병합
                          width: {
                            size: MAX_WIDTH - MAX_WIDTH / 10, // 두단: 4050(90%크기)
                            type: WidthType.DXA,
                          },
                        }),
                      ],
                    }),
                    // 하단 객관식 로우 반복
                    ...text.options.map((option, oIdx) => {
                      return new TableRow({
                        children: [
                          // 보기숫자
                          new TableCell({
                            children: [
                              new Paragraph({
                                children: [
                                  new TextRun({
                                    text: `${String.fromCharCode(9312 + oIdx)}`, // ①, ②, ③..
                                    bold: false,
                                    size: fontSize,
                                  }),
                                ],
                                spacing: {
                                  line: lineSpacing,
                                },
                                alignment: AlignmentType.LEFT,
                              }),
                            ],
                            borders: borderTransparent,
                            verticalAlign: VerticalAlign.TOP,
                            width: {
                              size: (MAX_WIDTH - MAX_WIDTH / 10) * 0.1, // 두단: 4050시 50%크기
                              type: WidthType.DXA,
                            },
                          }),
                          // 정답 셀
                          new TableCell({
                            children: [
                              new Paragraph({
                                children: [
                                  new TextRun({
                                    text: option,
                                    bold:
                                      paperTab ===
                                        PAPER_TABS.ANSWER_TEST_PAPER &&
                                      text.answer === oIdx + 1
                                        ? true
                                        : false,
                                    underline:
                                      paperTab ===
                                        PAPER_TABS.ANSWER_TEST_PAPER &&
                                      text.answer === oIdx + 1
                                        ? {}
                                        : { type: 'none' },
                                    size: fontSize,
                                  }),
                                ],
                                spacing: {
                                  line: lineSpacing,
                                },
                                alignment: AlignmentType.LEFT,
                              }),
                            ],
                            borders: borderTransparent,
                            verticalAlign: VerticalAlign.CENTER,
                            width: {
                              size: (MAX_WIDTH - MAX_WIDTH / 10) * 0.9, // 두단: 4050시 50%크기
                              type: WidthType.DXA,
                            },
                          }),
                        ],
                        verticalAlign: VerticalAlign.CENTER,
                        width: {
                          size: MAX_WIDTH - MAX_WIDTH / 10, // 두단: 4050(90%크기),
                          type: WidthType.DXA,
                        },
                      });
                    }),
                  ],
                  borders: borderTransparent,
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.TOP, // 수직 정렬: 가운데
              width: {
                size: MAX_WIDTH - MAX_WIDTH / 10, // 두단: 4050(90%크기)
                type: WidthType.DXA,
              },
            }),
          ],
          height: {
            value: lineSpacing, // 높이 설정 (Twip 단위: 500 = 약 0.35인치)
            rule: HeightRule.ATLEAST, // 높이를 자동으로 설정
          },
        }),
      ],
      width: {
        size: MAX_WIDTH, // 4500
        type: WidthType.DXA,
      },
      layout: TableLayoutType.FIXED, // 테이블 레이아웃을 고정
    });
  }
  // '정답만' 시험지
  else if (paperTab === PAPER_TABS.ANSWER) {
    result = new Table({
      rows: [
        new TableRow({
          children: [
            // 번호 셀
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${index}. `, // 왼쪽 인덱스
                      bold: false,
                      size: fontSize,
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.LEFT,
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.TOP,
              width: {
                size: 1000, // 두단: 450(10%크기),
                type: WidthType.DXA,
              },
            }),
            // 뜻
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      // text: text?.options[text?.answer - 1],
                      text: `${String.fromCharCode(9312 + text.answer - 1)}`,
                      bold: false,
                      size: fontSize,
                    }),
                  ],
                  spacing: {
                    line: lineSpacing,
                  },
                  alignment: AlignmentType.LEFT,
                }),
              ],
              borders: borderTransparent,
              verticalAlign: VerticalAlign.CENTER,
              width: {
                size: 9000, // 두단: 4050시 40%크기
                type: WidthType.DXA,
              },
            }),
          ],
          height: {
            value: lineSpacing, // 높이 설정 (Twip 단위: 500 = 약 0.35인치)
            rule: HeightRule.ATLEAST, // 높이를 자동으로 설정
          },
        }),
      ],
      width: {
        size: MAX_WIDTH, // 4500
        type: WidthType.DXA,
      },
      layout: TableLayoutType.FIXED, // 테이블 레이아웃을 고정
    });
  }
  return result;
};

// '어휘리스트'의 경우 공통 테이블 양식
const createCommonVocaListItem = (voca, fontSize, lineSpacing) => {
  return new Table({
    rows: [
      new TableRow({
        children: [
          // 영단어
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: voca?.eng,
                    bold: false,
                    size: fontSize,
                  }),
                ],
                spacing: {
                  line: lineSpacing,
                },
                alignment: AlignmentType.LEFT,
              }),
            ],
            borders: {
              insideH: { style: 'SINGLE', size: 4, color: '999999' }, // 내부 가로선
              insideV: { style: 'SINGLE', size: 4, color: '999999' }, // 내부 세로선
            },
            margins: {
              top: 60, // 3pt
              bottom: 60,
              left: 60,
              right: 60,
            },
            verticalAlign: VerticalAlign.TOP,
            width: {
              size: 5000, // 30% 너비
              type: WidthType.DXA,
            },
          }),
          // 한국어
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: voca?.kor,
                    bold: false,
                    size: fontSize,
                  }),
                ],
                spacing: {
                  line: lineSpacing,
                },
                alignment: AlignmentType.LEFT,
              }),
            ],
            borders: {
              insideH: { style: 'SINGLE', size: 4, color: '999999' }, // 내부 가로선
              insideV: { style: 'SINGLE', size: 4, color: '999999' }, // 내부 세로선
            },
            margins: {
              top: 60, // 3pt
              bottom: 60,
              left: 60,
              right: 60,
            },
            verticalAlign: VerticalAlign.TOP,
            width: {
              size: 5000, // 30% 너비
              type: WidthType.DXA,
            },
          }),
        ],
        height: {
          value: lineSpacing, // 높이 설정 (Twip 단위: 500 = 약 0.35인치)
          rule: HeightRule.ATLEAST, // 높이를 자동으로 설정
        },
      }),
    ],
    width: {
      size: MAX_WIDTH, // 4500
      type: WidthType.DXA,
    },
    layout: TableLayoutType.FIXED, // 테이블 레이아웃을 고정
  });
};

// 헤더 워터마크 추가
const createWatermarkHeader = base64Watermark => {
  return new Header({
    children: [
      new Paragraph({
        children: [
          new ImageRun({
            data: base64Watermark,
            transformation: {
              width: 300, // 워터마크 크기
              height: 100,
            },
            type: 'jpg', // 이미지 타입. 지정안하면 docx깨진 파일로 인식!!
            floating: {
              horizontalPosition: {
                relative: 'page',
                align: 'center',
              },
              verticalPosition: {
                relative: 'page',
                align: 'center',
              },
              behindText: true, // 본문보다 뒤쪽 배치
            },
          }),
        ],
        alignment: AlignmentType.CENTER,
      }),
    ],
  });
};

// 문서 생성 메인 함수
export const createDocument = (
  orgName,
  className,
  teacherName,
  paperName,
  columCount,
  fontSize,
  lineSpacing,
  vocaFirstWordShow,
  examKorShow,
  base64Img,
  generatedPaper,
  paperTab,
  commonPaperVocaList,
  logoAppearYN,
) => {
  // 폰트 사이즈
  let wordTextSize = 18;
  if (fontSize === PAPER_CONFIG.FONT_SIZE.SMALL) {
    wordTextSize = 14; // 7px
  } else if (fontSize === PAPER_CONFIG.FONT_SIZE.NORMAL) {
    wordTextSize = 18; // 9px
  } else if (fontSize === PAPER_CONFIG.FONT_SIZE.LARGE) {
    wordTextSize = 22; // 11px
  }
  // 줄간격
  let lineSpacingValue = 240;
  if (lineSpacing === PAPER_CONFIG.LINE_SPACING.SMALL) {
    lineSpacingValue = 193; // 0.8pt
  } else if (lineSpacing === PAPER_CONFIG.LINE_SPACING.NORMAL) {
    lineSpacingValue = 240; // 1.0pt
  } else if (lineSpacing === PAPER_CONFIG.LINE_SPACING.LARGE) {
    lineSpacingValue = 276; // 1.15pt
  }

  // '시험지/정답만/정답시험지' 13개 유형별로 항목 생성
  const makedItems = () => {
    let items = [];
    generatedPaper.forEach(type => {
      // 유형문제
      if (paperTab !== PAPER_TABS.ANSWER) {
        items.push(createSubject(type.title, wordTextSize, type.type));
      }
      // 답변부
      type.questions.forEach((voca, index) => {
        switch (type.type) {
          case 'A':
            items.push(
              createAtypeItem(
                type.startNo + index,
                voca,
                paperTab,
                wordTextSize,
                lineSpacingValue,
              ),
            );
            break;
          case 'B':
            items.push(
              createBtypeItem(
                type.startNo + index,
                voca,
                paperTab,
                wordTextSize,
                lineSpacingValue,
                vocaFirstWordShow,
              ),
            );
            break;
          case 'C':
            items.push(
              createCtypeItem(
                type.startNo + index,
                voca,
                paperTab,
                wordTextSize,
                lineSpacingValue,
                vocaFirstWordShow,
              ),
            );
            break;
          case 'D':
            items.push(
              createDtypeItem(
                type.startNo + index,
                voca,
                paperTab,
                wordTextSize,
                lineSpacingValue,
                vocaFirstWordShow,
              ),
            );
            break;
          case 'E':
            items.push(
              createEtypeItem(
                type.startNo + index,
                voca,
                paperTab,
                wordTextSize,
                lineSpacingValue,
                vocaFirstWordShow,
              ),
            );
            break;
          case 'F':
            items.push(
              createFtypeItem(
                type.startNo + index,
                voca,
                paperTab,
                wordTextSize,
                lineSpacingValue,
                vocaFirstWordShow,
                vocaFirstWordShow,
              ),
            );
            break;
          case 'G':
            items.push(
              createGtypeItem(
                type.startNo + index,
                voca,
                paperTab,
                wordTextSize,
                lineSpacingValue,
                vocaFirstWordShow,
              ),
            );
            break;
          case 'H':
            if (voca?.options?.length !== 4) return;
            items.push(
              createHtypeItem(
                type.startNo + index,
                voca,
                paperTab,
                wordTextSize,
                lineSpacingValue,
              ),
            );
            break;
          case 'I':
            if (voca?.options?.length !== 4) return;
            items.push(
              createItypeItem(
                type.startNo + index,
                voca,
                paperTab,
                wordTextSize,
                lineSpacingValue,
              ),
            );
            break;
          case 'J':
            if (voca?.options?.length !== 4) return;
            items.push(
              createJtypeItem(
                type.startNo + index,
                voca,
                paperTab,
                wordTextSize,
                lineSpacingValue,
                examKorShow,
              ),
            );
            break;
          case 'K':
            if (voca?.options?.length !== 4) return;
            items.push(
              createKtypeItem(
                type.startNo + index,
                voca,
                paperTab,
                wordTextSize,
                lineSpacingValue,
                examKorShow,
              ),
            );
            break;
          case 'L':
            if (voca?.options?.length !== 4) return;
            items.push(
              createLtypeItem(
                type.startNo + index,
                voca,
                paperTab,
                wordTextSize,
                lineSpacingValue,
              ),
            );
            break;
          case 'M':
            if (voca?.options?.length !== 4) return;
            items.push(
              createMtypeItem(
                type.startNo + index,
                voca,
                paperTab,
                wordTextSize,
                lineSpacingValue,
              ),
            );
            break;
          default:
            break;
        }
      });
    });
    return items;
  };

  const imageBuffer = Uint8Array.from(atob(base64Watermark), c =>
    c.charCodeAt(0),
  );

  // '어휘리스트' 시험지 전 유형 공통
  const makedCommonItems = () => {
    const items = [];
    commonPaperVocaList?.forEach(voca => {
      items.push(
        createCommonVocaListItem(voca, wordTextSize, lineSpacingValue),
      );
    });
    return items;
  };

  // 문서 생성
  const marginValue = 720;
  const document = new Document({
    styles: {
      paragraphStyles: [
        {
          id: 'Normal',
          name: 'Normal',
          run: {
            font: 'Malgun Gothic', // 맑은 고딕
            size: wordTextSize,
          },
        },
      ],
    },
    sections: [
      // 첫 번째 섹션: 헤더 (1단)
      {
        properties: {
          type: 'continuous',
          page: {
            margin: {
              top: marginValue,
              right: marginValue,
              left: marginValue,
              bottom: marginValue,
              header: 100, // 헤더상단 여백 최소로 조정
            },
          },
        },
        headers: {
          default: createWatermarkHeader(imageBuffer), // 모든 페이지에 적용
        },
        children: [
          createHeaderTable(
            orgName,
            className,
            teacherName,
            paperName,
            base64Img,
            wordTextSize,
            logoAppearYN,
          ),
        ],
      },
      // 두 번째 섹션: 본문 (2단)
      {
        properties: {
          type: 'continuous',
          page: {
            margin: {
              top: marginValue,
              right: marginValue,
              left: marginValue,
              bottom: marginValue,
            },
          },
          column: {
            count: columCount,
            space: 720, // 단 간격 설정
          },
        },
        children:
          paperTab !== PAPER_TABS.VOCABULARY_LIST
            ? makedItems(generatedPaper)
            : makedCommonItems(commonPaperVocaList),
      },
    ],
  });

  return document;
};
